import "./Portal.scss";

import { setUser } from "@sentry/react";
import { useAtomValue } from "jotai";
import { FC, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, Outlet, useLocation, useNavigate } from "react-router-dom";

import { Exclamation } from "@/assets/icons/icons";
import { AgentLoginBanner } from "@/components/Interface/Alert/AgentLoginBanner";
import { PortalTopBanners } from "@/components/Interface/Alert/PortalTopBanners";
import { Header } from "@/components/Layout/BaseLayouts/Header";
import { PortalPageLayout } from "@/components/Layout/BaseLayouts/PortalPageLayout";
import { BrandLoadingScreen } from "@/components/Layout/BrandLoadingScreen/BrandLoadingScreen";
import { ProfileCompletionPageLayout } from "@/components/Layout/ProfileCompletionPageLayout/ProfileCompletionPageLayout";
import { ScrollDirectionProvider } from "@/provider/ScrollDirectionProvider/ScrollDirectionProvider";
import { portalRoutes } from "@/router/routes/portalRoutes";
import { useGetCustomer } from "@/services/api";
import {
  isAgentAtom,
  isAnonymousUserAtom,
  showsSessionExpiredModalAtom,
} from "@/utils/atoms";
import { hasCompleteMandatoryPersonalData } from "@/utils/customerUtils";

// Layout and authentication wrapper for the portal.
export const Portal: FC = () => {
  const { t } = useTranslation("portal");
  const isAnonymousUser = useAtomValue(isAnonymousUserAtom);
  const isAgent = useAtomValue(isAgentAtom);
  const showSessionExpiredModal = useAtomValue(showsSessionExpiredModalAtom);
  const { pathname } = useLocation();

  const navigate = useNavigate();
  if (isAnonymousUser) {
    navigate(portalRoutes.welcome, { replace: true });
  }

  const { data: customerData, isLoading: isCustomerLoading } = useGetCustomer(
    undefined,
    {
      // For this check, we can safely cache it for 5 minutes.
      query: {
        enabled: !isAnonymousUser,
        refetchOnMount: "always",
      },
    },
  );

  useEffect(() => {
    setUser({ id: customerData?.id?.toString() });
  }, [customerData]);

  if (isCustomerLoading) {
    return <BrandLoadingScreen />;
  }

  if (customerData) {
    if (
      customerData.initialPasswordChanged === false &&
      pathname !== portalRoutes.initialPasswordSetup
    ) {
      return <Navigate to={portalRoutes.initialPasswordSetup} replace />;
    }

    if (
      customerData.initialPasswordChanged !== false &&
      !hasCompleteMandatoryPersonalData(customerData) &&
      pathname !== portalRoutes.personalDataOnboarding
    ) {
      return <Navigate to={portalRoutes.personalDataOnboarding} replace />;
    }

    const isProfileCompletionRoute =
      pathname === portalRoutes.initialPasswordSetup ||
      pathname === portalRoutes.personalDataOnboarding;

    if (isProfileCompletionRoute) {
      return (
        <ProfileCompletionPageLayout>
          <Outlet />
        </ProfileCompletionPageLayout>
      );
    }

    const bannerPlacement = pathname.startsWith(portalRoutes.cockpit.base)
      ? "cockpit"
      : null;

    return (
      <PortalPageLayout
        banners={
          <>
            {isAgent && <AgentLoginBanner />}
            {bannerPlacement && (
              // Below `desktop`, PortalPageLayout renders the fixed mobile
              // Header (z-20) above this in the DOM - `sticky top-0` (this
              // component's own base class) would then stick flush at the
              // viewport top, hidden behind it, in both the banner's normal
              // resting position and once actually stuck while scrolling.
              // `top-14` = 64px on this project's custom spacing scale,
              // clearing the header; `desktop:top-0` restores the plain
              // flush-top behavior once the Sidebar layout (no fixed header)
              // takes over.
              <PortalTopBanners
                placement={bannerPlacement}
                className="top-14 desktop:top-0"
              />
            )}
          </>
        }
      >
        <div className="col-span-full">
          <Outlet />
        </div>
      </PortalPageLayout>
    );
  }

  // Session expiry resolves customer fetches as null; the modal already
  // explains what happened — don't show the generic "Action failed." banner.
  if (showSessionExpiredModal) {
    return null;
  }

  return (
    <ScrollDirectionProvider>
      <div className="flex min-h-screen flex-col bg-white">
        <div className="desktop:hidden">
          <Header variant="portal" isMobile={true} />
        </div>
        <div className="flex w-full justify-center px-4 pb-8 pt-14 desktop:pt-8">
          <div className="flex w-full max-w-2xl items-center gap-4 rounded-[3px] bg-secondary-100 p-4 text-white">
            <Exclamation className="h-6 w-6 shrink-0" />
            <div className="flex flex-1 flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-base">{t("common.alerts.error")}</p>
            </div>
          </div>
        </div>
      </div>
    </ScrollDirectionProvider>
  );
};
